import { getAspectClass } from 'Editor/utils/fullscreenType'
import { cn } from '@/lib/utils'
import { cdnUrl } from 'commons/cdn'
import React, { useEffect, useState } from 'react'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
  DialogHeader,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { ChevronDownIcon } from '@radix-ui/react-icons'
import useSWR from 'swr'
import { useParams } from 'react-router-dom'
import { Separator } from '@/components/ui/separator'
import { useEditorStore } from 'CanvasCore/uiSlice'
import { applyStaticOutfitToEditor } from 'Editor/lib/applyStaticOutfit'
import { AvatarMediaPreview } from 'AdminPanel/SpineAvatars/SpineAvatars/components/AvatarMediaPreview'
import MyPagination from 'components/ui/MyPagination'
import MyRefetchButton from 'components/ui/MyRefetchButton'
import { resolveAvatarOutfit } from 'commons/avatarOutfit'
import { toast } from '@/components/ui/use-toast'

const PAGE_SIZE = 60

/** Cover / Preview 需要静态图 url；无 url 视为生图未完成，不可选用 */
const hasAvatarImage = (avatar) => Boolean(avatar?.url)

const AvatarPicker = ({ original, template, form, children, onSubmit }) => {
  const { tid } = useParams()
  const [open, setOpen] = useState(false)
  const [after, setAfter] = useState(null)
  const [afters, setAfters] = useState([])
  const [selected, setSelected] = useState({ id: original })

  const { data: avatars, mutate: mutateList } = useSWR(
    open
      ? [
          '/avatars',
          {
            tid,
            state: 0,
            limit: PAGE_SIZE,
            ...(after ? { after } : {}),
          },
        ]
      : null,
  )

  // 当前 Cover / Preview 直接按模板里存的 avatar id 拉完整 avatar，置顶展示。
  // 拉回来的对象自带 url/视频/drawData，无需再从表单拼；没有 id 就不置顶。
  const initDataId = form
    ? form.watch('initDataId', template?.initDataId)
    : undefined
  const previewId = form
    ? form.watch('previewId', template?.previewId)
    : undefined

  const { data: coverAvatar, mutate: mutateCover } = useSWR(
    open && initDataId ? ['/avatars/' + initDataId] : null,
  )
  const { data: previewAvatar, mutate: mutatePreview } = useSWR(
    open && previewId ? ['/avatars/' + previewId] : null,
  )

  // 置顶当前 Cover / Preview，后面正常分页请求 60 个，重复不去重
  const pinned = []
  if (coverAvatar) pinned.push({ ...coverAvatar, badge: 'Cover' })
  if (previewAvatar) pinned.push({ ...previewAvatar, badge: 'Preview' })

  // 打开时 selected 只有 id；等列表/置顶数据回来后补全 url，已有图的可直接保存
  useEffect(() => {
    if (!open || !selected?.id || hasAvatarImage(selected)) return
    const candidates = [coverAvatar, previewAvatar, ...(avatars || [])].filter(
      Boolean,
    )
    const found = candidates.find(
      (a) => a.id === selected.id && hasAvatarImage(a),
    )
    if (found) setSelected(found)
  }, [open, selected, coverAvatar, previewAvatar, avatars])

  const keyOf = (a) => a?.id || a?.url
  const isSelected = (a) => !!keyOf(selected) && keyOf(selected) === keyOf(a)

  const nextAfter =
    (avatars?.length ?? 0) === PAGE_SIZE
      ? avatars[avatars.length - 1]?.id
      : null

  const goAfter = (next) => {
    setAfter(next)
    if (next === null) {
      setAfters([])
    } else {
      const index = afters.indexOf(next)
      if (index === -1) setAfters([...afters, next])
    }
  }

  const handleOpenChange = (v) => {
    setOpen(v)
    if (!v) {
      setAfter(null)
      setAfters([])
      setSelected({ id: original })
    }
  }

  const handleRefresh = async () => {
    await Promise.all([
      mutateList(),
      initDataId ? mutateCover() : Promise.resolve(),
      previewId ? mutatePreview() : Promise.resolve(),
    ])
  }

  const handleSelectAvatar = (avatar) => {
    if (!hasAvatarImage(avatar)) return
    setSelected(avatar)
  }

  const handleSubmit = () => {
    if (!hasAvatarImage(selected)) return
    onSubmit(selected)
    setOpen(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-h-[80vh] gap-4">
        <DialogHeader>
          <DialogTitle>选择 Avatar</DialogTitle>
          <DialogDescription>
            从已上传的 Avatar 中选择。图片未生成完成的不能用作 Cover /
            Preview，可点刷新查看最新状态。
          </DialogDescription>
        </DialogHeader>
        <Separator className="my-2" />
        <div className="flex flex-wrap items-center gap-4">
          <MyPagination
            nextAfter={nextAfter}
            after={after}
            goAfter={goAfter}
            afters={afters}
            className="m-0 w-auto"
          />
          <MyRefetchButton onClick={handleRefresh} />
        </div>
        <div className="flex-1 overflow-y-auto max-h-[50vh]">
          <div className="grid grid-cols-4 gap-2 px-1">
            {pinned.map((avatar) => (
              <AvatarItem
                key={`pinned-${avatar.badge}`}
                avatar={avatar}
                badge={avatar.badge}
                selected={isSelected(avatar)}
                onClick={() => handleSelectAvatar(avatar)}
                template={template}
                setOpen={setOpen}
              />
            ))}
            {avatars?.map((avatar) => (
              <AvatarItem
                key={avatar.id}
                avatar={avatar}
                selected={isSelected(avatar)}
                onClick={() => handleSelectAvatar(avatar)}
                template={template}
                setOpen={setOpen}
              />
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={handleSubmit} disabled={!hasAvatarImage(selected)}>
            Select
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const AvatarItem = ({ avatar, selected, badge, template, setOpen, ...props }) => {
  const { setDrawers, setTonings } = useEditorStore((state) => state)
  const aspectClass = getAspectClass(
    avatar?.fullscreenType ?? template?.fullscreenType,
  )
  const ready = hasAvatarImage(avatar)

  const handleGoToView = () => {
    window.open(`/review/avatar/${template.id}?avatarId=${avatar.id}`, '_blank')
    setDrawers(template.id, [])
    setTonings(template.id, {})
  }

  const handleGoToEditor = async () => {
    try {
      const outfit = await resolveAvatarOutfit(avatar)
      const tid = outfit.tid || avatar.tid
      await applyStaticOutfitToEditor(tid, outfit)
      setTimeout(() => {
        setOpen(false)
      }, 300)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed',
        description: err instanceof Error ? err.message : 'Missing drawData',
      })
    }
  }

  return (
    <div className="flex flex-col items-center relative" {...props}>
      <div
        className={cn(
          'bg-white border-2 border-transparent rounded-xl w-full',
          'flex items-center justify-center group',
          'hover:border-primary',
          selected && 'border-primary',
          !ready && 'opacity-60',
        )}
      >
        <div className="w-full h-full relative">
          {badge && (
            <div className="absolute top-1 left-1 z-10 rounded bg-black/45 px-1 py-0 text-[9px] font-medium text-white">
              {badge}
            </div>
          )}
          {!ready && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-muted/80">
              <span className="text-[10px] font-medium text-muted-foreground">
                生成中
              </span>
            </div>
          )}
          {avatar?.engineWith === 'spine' &&
          (avatar?.videoPreviewUrl || avatar?.videoUrl) ? (
            <AvatarMediaPreview
              videoUrl={avatar.videoPreviewUrl || avatar.videoUrl}
              imageUrl={avatar.thumb ?? avatar.url}
              className={cn('w-full h-full rounded-xl', aspectClass)}
            />
          ) : ready ? (
            <img
              alt="..."
              className={cn(
                'w-full h-full object-contain rounded-xl',
                aspectClass,
              )}
              src={cdnUrl(avatar?.thumb ?? avatar.url)}
            />
          ) : (
            <div className={cn('w-full rounded-xl bg-muted', aspectClass)} />
          )}
          {avatar?.id && (
            <div className="absolute top-1 right-1">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="h-6 w-6 p-0 bg-white/80 hover:bg-white"
                  >
                    <ChevronDownIcon className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleGoToView}>
                    Go to view
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleGoToEditor}>
                    Go to editor
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AvatarPicker
